import React, { useCallback, useMemo, useState } from 'react';
import styles from './Service.module.scss';
import Link from 'next/link';
import type { ServiceProps } from './Services.type';
import { isArray } from 'lodash';
import useModalState from '../_hooks/useModalState';
import { Modal } from '../_shared';
import { SendOrderForm } from '../_shared/SendOrderForm';
import { validationSchema } from '../_shared/SendOrderForm/validationSchema';
import clsx from 'clsx';
import { sendMessage } from './sendMessageApi';
import BeatLoader from 'react-spinners/BeatLoader';

export const Service: React.FC<ServiceProps> = React.memo(
    ({ service, activeTab }) => {
        const [isFormModalVisible, showFormModal, hideFormModal] =
            useModalState();
        const [isSuccessModalVisible, showSuccessModal, hideSuccessModal] =
            useModalState();
        const [isBusy, setIsBusy] = useState(false);
        const [successState, setSuccessState] = useState({
            header: 'Ошибка!',
            message: 'Что-то пошло не так. Пожалуйста, попробуйте еще раз',
        });
        const longPreferences = useMemo(
            () => service?.longPreferences,
            [service?.longPreferences]
        );
        const handleFormSubmit = useCallback(
            async (data) => {
                try {
                    setIsBusy(true);
                    await sendMessage({
                        ...data,
                        role:
                            activeTab === 'customers'
                                ? 'customer'
                                : 'participant',
                        serviceTitle: service?.title,
                    });

                    setSuccessState({
                        header: 'Спасибо!',
                        message: 'Скоро мы с Вами свяжемся.',
                    });
                } finally {
                    hideFormModal();
                    showSuccessModal();
                    setIsBusy(false);
                }
            },
            [activeTab, hideFormModal, service?.title, showSuccessModal]
        );
        if (!service) {
            return null;
        }

        return (
            <div className={styles.container}>
                <h1 className={styles.title}>
                    {`Подробнее о сервисе «${service?.title}»`}
                </h1>
                <div className={styles.description}>
                    {longPreferences?.map((pr, idx) => {
                        return (
                            <>
                                {idx === 3 && service?.videoLink && (
                                    <div className={styles.video}>
                                        <video controls width="490">
                                            <source
                                                src={service?.videoLink}
                                                type="video/mp4"
                                            />
                                        </video>
                                        <p>{`Как работает сервис «${service.title}»?`}</p>
                                    </div>
                                )}
                                <div
                                    key={`${pr.title}-${idx}`}
                                    className={styles.descrTile}
                                >
                                    <div>
                                        <h4>{pr.title}</h4>
                                        {isArray(pr.text) ? (
                                            <ul>
                                                {pr.text.map((t) => (
                                                    <li key={t}>{t}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p>{pr.text}</p>
                                        )}
                                    </div>
                                    <div>{`0${idx + 1}`}</div>
                                </div>
                            </>
                        );
                    })}
                </div>
                <div className={styles.conditions}>
                    <h4>Условия подключения</h4>
                    <div className={styles.conditionsWrapper}>
                        {service?.conditions?.map((condition, idx) => {
                            return (
                                <div
                                    className={clsx(
                                        styles.condition,
                                        idx === 2 && styles.thirdCondition
                                    )}
                                    key={condition.title}
                                >
                                    <h5>{condition.title}</h5>
                                    <ul>
                                        {condition.texts.map((t, idy) => {
                                            return (
                                                <li key={`${t.text}-${idy}`}>
                                                    <p>{t.text}</p>
                                                    {t.price ? (
                                                        t.link ? (
                                                            <Link href={t.link}>
                                                                <a
                                                                    className={
                                                                        styles.textLink
                                                                    }
                                                                >
                                                                    {t.price}
                                                                </a>
                                                            </Link>
                                                        ) : (
                                                            <p>{t.price}</p>
                                                        )
                                                    ) : null}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.action} onClick={showFormModal}>
                        Оформить
                    </div>
                    {isFormModalVisible && (
                        <Modal
                            onHide={hideFormModal}
                            header="Оставить заявку"
                            className={styles.sendOrderModal}
                        >
                            {isBusy && (
                                <div className={styles.loader}>
                                    <BeatLoader color="#2d9cdb" />
                                </div>
                            )}
                            <SendOrderForm
                                handleSubmit={handleFormSubmit}
                                validationSchema={validationSchema}
                                textAreLabel="Ваше сообщение"
                                contactLabel="Номер телефона"
                            />
                        </Modal>
                    )}

                    {isSuccessModalVisible && (
                        <Modal
                            onHide={hideSuccessModal}
                            header={successState.header}
                            className={styles.responseModal}
                        >
                            <p>{successState.message}</p>
                        </Modal>
                    )}
                </div>
            </div>
        );
    }
);
